// frontend/src/pages/Agent.jsx
import { useState, useEffect, useCallback, useRef } from 'react';
import { agentAPI } from '../services/api';
import {
  Card, Button, Badge, StatCard, Modal, Input, Select,
  Spinner, Empty, Table, Td, SectionHeader, ProgressBar,
} from '../components/ui';
import toast from 'react-hot-toast';
import { clsx } from 'clsx';
import {
  ArrowDownCircle, ArrowUpCircle, Search, RefreshCw,
  User, Phone, Mail, ShieldCheck, ShieldAlert, Download,
  TrendingUp, TrendingDown, Wallet, Award, FileText,
  ChevronLeft, ChevronRight, AlertCircle, Plus,
} from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) =>
  typeof n === 'number'
    ? n.toLocaleString('en-UG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
    : '0';

const fmtMoney = (n, currency = 'UGX') =>
  `${currency} ${fmt(n)}`;

const safeFmt = (dateStr) => {
  try {
    const d = typeof dateStr === 'string' ? parseISO(dateStr) : new Date(dateStr);
    return isValid(d) ? format(d, 'dd MMM yyyy HH:mm') : '—';
  } catch {
    return '—';
  }
};

const TIER_COLOR = {
  bronze:   'amber',
  silver:   'gray',
  gold:     'gold',
  platinum: 'cyan',
};

const TIER_ICON = {
  bronze:   '🥉',
  silver:   '🥈',
  gold:     '🥇',
  platinum: '💎',
};

const TX_COLOR = {
  cash_in:  'green',
  cash_out: 'amber',
};

const STATUS_COLOR = {
  completed: 'green',
  pending:   'amber',
  failed:    'red',
  reversed:  'gray',
};

const TABS = [
  { id: 'console',     label: 'Console' },
  { id: 'customers',   label: 'Customer Search' },
  { id: 'transactions',label: 'Transactions' },
  { id: 'eod',         label: 'EOD Report' },
];

// ── KYC Badge ─────────────────────────────────────────────────────────────────
function KycBadge({ status }) {
  if (!status) return <Badge label="No KYC" color="gray" />;
  const map = { verified: 'green', pending: 'amber', rejected: 'red' };
  return (
    <Badge
      label={status.charAt(0).toUpperCase() + status.slice(1)}
      color={map[status] || 'gray'}
    />
  );
}

// ── Customer Card (small) ─────────────────────────────────────────────────────
function CustomerCard({ customer, onCashIn, onCashOut, compact = false }) {
  if (!customer) return null;
  return (
    <div className={clsx(
      'rounded-xl border border-border bg-bg-panel p-4 flex flex-col gap-3',
      compact && 'p-3'
    )}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-cyan/15 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-cyan" />
          </div>
          <div>
            <p className="text-text-primary font-bold text-sm">{customer.name || '—'}</p>
            <p className="text-text-secondary text-xs mt-0.5">{customer.phone || '—'}</p>
            {customer.email && (
              <p className="text-text-muted text-xs">{customer.email}</p>
            )}
          </div>
        </div>
        <KycBadge status={customer.kyc} />
      </div>
      {(onCashIn || onCashOut) && (
        <div className="flex gap-2 pt-1">
          {onCashIn && (
            <Button variant="success" size="sm" className="flex-1" onClick={onCashIn}>
              <ArrowDownCircle className="w-4 h-4" />
              Cash In
            </Button>
          )}
          {onCashOut && (
            <Button variant="outline" size="sm" className="flex-1" onClick={onCashOut}>
              <ArrowUpCircle className="w-4 h-4" />
              Cash Out
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Customer Search Input ─────────────────────────────────────────────────────
function CustomerSearchField({ value, onChange, onSelect, results, searching, label }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(results.length > 0);
  }, [results]);

  return (
    <div className="relative flex flex-col gap-1.5" ref={ref}>
      {label && (
        <label className="text-xs font-bold text-text-muted uppercase tracking-wider">{label}</label>
      )}
      <div className="relative">
        <input
          className={clsx(
            'w-full bg-bg-panel border border-border rounded-xl px-4 py-3 pr-10 text-text-primary text-sm',
            'placeholder:text-text-muted outline-none transition-colors',
            'focus:border-cyan focus:ring-1 focus:ring-cyan/30'
          )}
          placeholder="Search by phone or name..."
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => results.length > 0 && setOpen(true)}
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {searching ? (
            <div className="w-4 h-4 border-2 border-cyan border-t-transparent rounded-full animate-spin" />
          ) : (
            <Search className="w-4 h-4 text-text-muted" />
          )}
        </div>
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 z-20 mt-1 bg-bg-surface border border-border rounded-xl shadow-card overflow-hidden">
          {results.map((u) => (
            <button
              key={u._id}
              className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-elevated transition-colors text-left"
              onClick={() => {
                onSelect(u);
                setOpen(false);
              }}
            >
              <User className="w-4 h-4 text-cyan flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-text-primary text-sm font-semibold truncate">{u.name}</p>
                <p className="text-text-muted text-xs">{u.phone}</p>
              </div>
              <KycBadge status={u.kyc} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Cash Modal (shared for cash-in & cash-out) ────────────────────────────────
function CashModal({ open, onClose, mode, prefillCustomer, onSuccess }) {
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [customer, setCustomer] = useState(prefillCustomer || null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const searchTimer = useRef(null);

  // Sync prefill customer when modal opens
  useEffect(() => {
    if (open) {
      setCustomer(prefillCustomer || null);
      setQuery(prefillCustomer ? prefillCustomer.phone : '');
      setAmount('');
      setNote('');
      setSearchResults([]);
    }
  }, [open, prefillCustomer]);

  const handleQueryChange = (val) => {
    setQuery(val);
    setCustomer(null);
    clearTimeout(searchTimer.current);
    if (val.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await agentAPI.searchCustomer(val.trim());
        setSearchResults(res.users || []);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const handleSelect = (u) => {
    setCustomer(u);
    setQuery(u.phone);
    setSearchResults([]);
  };

  const handleSubmit = async () => {
    if (!customer) {
      toast.error('Select a customer first');
      return;
    }
    const amt = parseFloat(amount);
    if (!amt || amt < 1) {
      toast.error('Enter a valid amount');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { customerPhone: customer.phone, amount: amt, note: note || undefined };
      if (mode === 'cash_in') {
        await agentAPI.cashIn(payload);
        toast.success(`Cash In of ${fmtMoney(amt)} completed`);
      } else {
        await agentAPI.cashOut(payload);
        toast.success(`Cash Out of ${fmtMoney(amt)} completed`);
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || `${mode === 'cash_in' ? 'Cash In' : 'Cash Out'} failed`);
    } finally {
      setSubmitting(false);
    }
  };

  const isCashIn = mode === 'cash_in';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isCashIn ? 'Cash In — Credit Customer' : 'Cash Out — Debit Customer'}
      size="md"
    >
      <div className="flex flex-col gap-4">
        {/* Step 1: Customer */}
        <div>
          <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">1. Find Customer</p>
          <CustomerSearchField
            value={query}
            onChange={handleQueryChange}
            onSelect={handleSelect}
            results={searchResults}
            searching={searching}
          />
          {customer && (
            <div className="mt-3">
              <CustomerCard customer={customer} compact />
            </div>
          )}
        </div>

        {/* Step 2: Amount + Note */}
        <div>
          <p className="text-xs font-bold text-text-muted uppercase tracking-wider mb-2">2. Transaction Details</p>
          <div className="flex flex-col gap-3">
            <Input
              label="Amount (UGX)"
              type="number"
              min="1"
              placeholder="e.g. 50000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <Input
              label="Note (optional)"
              type="text"
              placeholder="Transaction reference or note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        {/* Summary */}
        {customer && amount && parseFloat(amount) > 0 && (
          <div className={clsx(
            'rounded-xl p-3 border text-sm flex items-center gap-3',
            isCashIn
              ? 'bg-green/10 border-green/30 text-green'
              : 'bg-amber/10 border-amber/30 text-amber'
          )}>
            {isCashIn
              ? <ArrowDownCircle className="w-5 h-5 flex-shrink-0" />
              : <ArrowUpCircle className="w-5 h-5 flex-shrink-0" />
            }
            <span>
              {isCashIn ? 'Credit' : 'Debit'}{' '}
              <strong>{customer.name}</strong>{' '}
              {isCashIn ? 'with' : 'of'}{' '}
              <strong>{fmtMoney(parseFloat(amount))}</strong>
            </span>
          </div>
        )}

        <div className="flex gap-3 pt-1">
          <Button variant="secondary" size="md" className="flex-1" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={isCashIn ? 'success' : 'outline'}
            size="md"
            className="flex-1"
            loading={submitting}
            disabled={!customer || !amount}
            onClick={handleSubmit}
          >
            {isCashIn ? 'Confirm Cash In' : 'Confirm Cash Out'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Float Request Modal ───────────────────────────────────────────────────────
function FloatRequestModal({ open, onClose, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [note, setNote]   = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) { setAmount(''); setNote(''); }
  }, [open]);

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt < 1) { toast.error('Enter a valid amount'); return; }
    setLoading(true);
    try {
      await agentAPI.requestFloat({ amount: amt, note: note || undefined });
      toast.success('Float request submitted to branch');
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err?.message || 'Float request failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Request Float Top-up" size="sm">
      <div className="flex flex-col gap-4">
        <Input
          label="Amount (UGX)"
          type="number"
          min="1"
          placeholder="e.g. 500000"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <Input
          label="Note (optional)"
          type="text"
          placeholder="Reason for request"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="flex gap-3 pt-1">
          <Button variant="secondary" size="md" className="flex-1" onClick={onClose} disabled={loading}>Cancel</Button>
          <Button variant="primary" size="md" className="flex-1" loading={loading} onClick={handleSubmit}>
            Request Float
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Console Tab ───────────────────────────────────────────────────────────────
function ConsoleTab({ profile, floatData, profileLoading, onRefresh }) {
  const [cashInModal, setCashInModal]   = useState(false);
  const [cashOutModal, setCashOutModal] = useState(false);
  const [floatModal, setFloatModal]     = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [txLoading, setTxLoading]       = useState(true);

  const fetchRecentTx = useCallback(async () => {
    setTxLoading(true);
    try {
      const res = await agentAPI.getTransactions({ page: 1, limit: 10 });
      setTransactions(res.transactions || []);
    } catch {
      // silent — full list in Transactions tab
    } finally {
      setTxLoading(false);
    }
  }, []);

  useEffect(() => { fetchRecentTx(); }, [fetchRecentTx]);

  const handleSuccess = () => {
    fetchRecentTx();
    onRefresh();
  };

  const agent   = profile?.agent || {};
  const float   = floatData?.float || {};
  const tier    = (agent.tier || 'bronze').toLowerCase();

  // Derive today's cashIn / cashOut from recent transactions
  const today = format(new Date(), 'yyyy-MM-dd');
  const todayTx = transactions.filter((t) => {
    try {
      return format(parseISO(t.createdAt || t.date), 'yyyy-MM-dd') === today;
    } catch { return false; }
  });
  const todayCashIn  = todayTx.filter((t) => t.type === 'cash_in').reduce((s, t) => s + (t.amount || 0), 0);
  const todayCashOut = todayTx.filter((t) => t.type === 'cash_out').reduce((s, t) => s + (t.amount || 0), 0);
  const todayComm    = todayTx.reduce((s, t) => s + (t.commission || 0), 0);

  return (
    <div className="flex flex-col gap-6">
      {/* Agent Identity Strip */}
      <Card className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-4 flex-1">
          <div className="w-14 h-14 rounded-2xl bg-cyan/15 flex items-center justify-center text-2xl flex-shrink-0">
            {TIER_ICON[tier] || '🏪'}
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <p className="text-text-primary font-bold text-base">
                {agent.businessName || 'My Agent Business'}
              </p>
              <Badge
                label={tier}
                color={TIER_COLOR[tier] || 'gray'}
              />
              {agent.isActive === false && (
                <Badge label="Inactive" color="red" />
              )}
            </div>
            <p className="text-text-secondary text-sm mt-0.5">
              Code: <span className="font-mono font-bold text-cyan">{agent.code || '—'}</span>
              {agent.branchId && (
                <span className="ml-3 text-text-muted">Branch: {agent.branchId}</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="ghost" size="sm" onClick={onRefresh}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setFloatModal(true)}>
            <Plus className="w-4 h-4" />
            Request Float
          </Button>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Float Balance"
          value={fmtMoney(agent.floatBalance ?? float.balance ?? 0)}
          icon={<Wallet className="w-6 h-6" />}
          color="#06C8E8"
          loading={profileLoading}
        />
        <StatCard
          label="Today's Cash In"
          value={fmtMoney(todayCashIn)}
          icon={<ArrowDownCircle className="w-6 h-6" />}
          color="#10B981"
          loading={txLoading}
        />
        <StatCard
          label="Today's Cash Out"
          value={fmtMoney(todayCashOut)}
          icon={<ArrowUpCircle className="w-6 h-6" />}
          color="#F59E0B"
          loading={txLoading}
        />
        <StatCard
          label="Commission Earned"
          value={fmtMoney(agent.commissionBalance ?? todayComm)}
          icon={<Award className="w-6 h-6" />}
          color="#8B5CF6"
          loading={profileLoading}
        />
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Card className="flex flex-col items-center justify-center gap-3 py-8 cursor-pointer hover:border-green/50 transition-colors"
          onClick={() => setCashInModal(true)}
        >
          <div className="w-16 h-16 rounded-2xl bg-green/15 flex items-center justify-center">
            <ArrowDownCircle className="w-8 h-8 text-green" />
          </div>
          <div className="text-center">
            <p className="text-text-primary font-bold text-base">Cash In</p>
            <p className="text-text-muted text-xs mt-1">Credit customer wallet · Debit float</p>
          </div>
          <Button variant="success" size="md">Start Cash In</Button>
        </Card>

        <Card className="flex flex-col items-center justify-center gap-3 py-8 cursor-pointer hover:border-amber/50 transition-colors"
          onClick={() => setCashOutModal(true)}
        >
          <div className="w-16 h-16 rounded-2xl bg-amber/15 flex items-center justify-center">
            <ArrowUpCircle className="w-8 h-8 text-amber" />
          </div>
          <div className="text-center">
            <p className="text-text-primary font-bold text-base">Cash Out</p>
            <p className="text-text-muted text-xs mt-1">Debit customer wallet · Credit float</p>
          </div>
          <Button variant="outline" size="md">Start Cash Out</Button>
        </Card>
      </div>

      {/* Recent Transactions */}
      <Card noPad>
        <div className="p-5 border-b border-border">
          <SectionHeader title="Recent Transactions" />
        </div>
        {txLoading ? (
          <div className="flex justify-center py-10">
            <Spinner size="lg" />
          </div>
        ) : transactions.length === 0 ? (
          <Empty
            icon={<FileText className="w-10 h-10 text-text-muted" />}
            title="No transactions yet"
            description="Cash in or cash out to get started"
          />
        ) : (
          <div className="p-5">
            <Table headers={['Type', 'Customer', 'Amount', 'Status', 'Time']}>
              {transactions.slice(0, 8).map((tx, i) => (
                <tr key={tx._id || i}>
                  <Td>
                    <Badge
                      label={tx.type === 'cash_in' ? 'Cash In' : tx.type === 'cash_out' ? 'Cash Out' : tx.type || '—'}
                      color={TX_COLOR[tx.type] || 'gray'}
                    />
                  </Td>
                  <Td className="text-text-primary font-medium">{tx.customerName || tx.customer || '—'}</Td>
                  <Td className={clsx('font-bold', tx.type === 'cash_in' ? 'text-green' : 'text-amber')}>
                    {tx.type === 'cash_in' ? '+' : '-'}{fmtMoney(tx.amount || 0)}
                  </Td>
                  <Td>
                    <Badge
                      label={tx.status || 'completed'}
                      color={STATUS_COLOR[tx.status] || 'green'}
                    />
                  </Td>
                  <Td className="text-text-muted text-xs">{safeFmt(tx.createdAt || tx.date)}</Td>
                </tr>
              ))}
            </Table>
          </div>
        )}
      </Card>

      {/* Modals */}
      <CashModal
        open={cashInModal}
        onClose={() => setCashInModal(false)}
        mode="cash_in"
        onSuccess={handleSuccess}
      />
      <CashModal
        open={cashOutModal}
        onClose={() => setCashOutModal(false)}
        mode="cash_out"
        onSuccess={handleSuccess}
      />
      <FloatRequestModal
        open={floatModal}
        onClose={() => setFloatModal(false)}
        onSuccess={onRefresh}
      />
    </div>
  );
}

// ── Customer Search Tab ───────────────────────────────────────────────────────
function CustomerSearchTab() {
  const [query, setQuery]         = useState('');
  const [results, setResults]     = useState([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [cashInModal, setCashInModal]   = useState(false);
  const [cashOutModal, setCashOutModal] = useState(false);
  const searchTimer = useRef(null);

  const handleQueryChange = (val) => {
    setQuery(val);
    setSelected(null);
    clearTimeout(searchTimer.current);
    if (val.trim().length < 3) { setResults([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await agentAPI.searchCustomer(val.trim());
        setResults(res.users || []);
      } catch (err) {
        toast.error(err?.message || 'Search failed');
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 400);
  };

  const handleSelect = (u) => {
    setSelected(u);
    setQuery(u.phone);
    setResults([]);
  };

  return (
    <div className="flex flex-col gap-6 max-w-2xl mx-auto">
      <Card>
        <SectionHeader title="Find a Customer" />
        <CustomerSearchField
          value={query}
          onChange={handleQueryChange}
          onSelect={handleSelect}
          results={results}
          searching={searching}
        />
      </Card>

      {selected ? (
        <Card className="flex flex-col gap-4">
          <p className="text-xs font-bold text-text-muted uppercase tracking-wider">Customer Profile</p>
          <div className="flex flex-col sm:flex-row sm:items-start gap-4">
            <div className="w-16 h-16 rounded-2xl bg-cyan/15 flex items-center justify-center flex-shrink-0">
              <User className="w-8 h-8 text-cyan" />
            </div>
            <div className="flex-1 flex flex-col gap-2">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="text-text-primary font-bold text-lg">{selected.name}</p>
                <KycBadge status={selected.kyc} />
              </div>
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2 text-sm text-text-secondary">
                  <Phone className="w-4 h-4 text-text-muted" />
                  {selected.phone}
                </div>
                {selected.email && (
                  <div className="flex items-center gap-2 text-sm text-text-secondary">
                    <Mail className="w-4 h-4 text-text-muted" />
                    {selected.email}
                  </div>
                )}
              </div>
              {selected.kyc === 'verified' ? (
                <div className="flex items-center gap-2 text-xs text-green mt-1">
                  <ShieldCheck className="w-4 h-4" />
                  KYC Verified — eligible for all transactions
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-amber mt-1">
                  <ShieldAlert className="w-4 h-4" />
                  KYC {selected.kyc || 'not submitted'} — limits may apply
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-border">
            <Button variant="success" size="md" onClick={() => setCashInModal(true)}>
              <ArrowDownCircle className="w-4 h-4" />
              Cash In for {selected.name.split(' ')[0]}
            </Button>
            <Button variant="outline" size="md" onClick={() => setCashOutModal(true)}>
              <ArrowUpCircle className="w-4 h-4" />
              Cash Out for {selected.name.split(' ')[0]}
            </Button>
          </div>
        </Card>
      ) : query.length >= 3 && !searching && results.length === 0 ? (
        <Card>
          <Empty
            icon={<AlertCircle className="w-10 h-10 text-text-muted" />}
            title="No customer found"
            description={`No customer matched "${query}". Check the phone number or name.`}
          />
        </Card>
      ) : !query && (
        <Card>
          <Empty
            icon={<Search className="w-10 h-10 text-text-muted" />}
            title="Search for a customer"
            description="Enter at least 3 characters of a phone number or name to find a customer"
          />
        </Card>
      )}

      <CashModal
        open={cashInModal}
        onClose={() => setCashInModal(false)}
        mode="cash_in"
        prefillCustomer={selected}
        onSuccess={() => {}}
      />
      <CashModal
        open={cashOutModal}
        onClose={() => setCashOutModal(false)}
        mode="cash_out"
        prefillCustomer={selected}
        onSuccess={() => {}}
      />
    </div>
  );
}

// ── Transactions Tab ──────────────────────────────────────────────────────────
function TransactionsTab() {
  const [transactions, setTransactions] = useState([]);
  const [pagination, setPagination]     = useState({ total: 0, pages: 1 });
  const [loading, setLoading]           = useState(true);
  const [page, setPage]                 = useState(1);
  const [typeFilter, setTypeFilter]     = useState('all');

  const fetchTx = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 15 };
      if (typeFilter !== 'all') params.type = typeFilter;
      const res = await agentAPI.getTransactions(params);
      setTransactions(res.transactions || []);
      setPagination(res.pagination || { total: 0, pages: 1 });
    } catch (err) {
      toast.error(err?.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  }, [page, typeFilter]);

  useEffect(() => { fetchTx(); }, [fetchTx]);

  useEffect(() => { setPage(1); }, [typeFilter]);

  return (
    <div className="flex flex-col gap-4">
      {/* Filters */}
      <Card className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex gap-2 flex-wrap">
          {['all', 'cash_in', 'cash_out'].map((f) => (
            <button
              key={f}
              onClick={() => setTypeFilter(f)}
              className={clsx(
                'px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wider transition-colors border',
                typeFilter === f
                  ? 'bg-cyan/20 border-cyan text-cyan'
                  : 'border-border text-text-muted hover:border-border-hi hover:text-text-primary'
              )}
            >
              {f === 'all' ? 'All' : f === 'cash_in' ? 'Cash In' : 'Cash Out'}
            </button>
          ))}
        </div>
        <div className="sm:ml-auto text-xs text-text-muted">
          {pagination.total} transaction{pagination.total !== 1 ? 's' : ''}
        </div>
        <Button variant="ghost" size="sm" onClick={fetchTx}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </Card>

      {/* Table */}
      <Card noPad>
        {loading ? (
          <div className="flex justify-center py-14">
            <Spinner size="lg" />
          </div>
        ) : transactions.length === 0 ? (
          <Empty
            icon={<FileText className="w-10 h-10 text-text-muted" />}
            title="No transactions found"
            description={typeFilter !== 'all' ? `No ${typeFilter.replace('_', ' ')} transactions found` : 'No transactions yet'}
          />
        ) : (
          <div className="p-5">
            <Table headers={['Type', 'Customer', 'Phone', 'Amount', 'Status', 'Note', 'Time']}>
              {transactions.map((tx, i) => (
                <tr key={tx._id || i}>
                  <Td>
                    <Badge
                      label={tx.type === 'cash_in' ? 'Cash In' : tx.type === 'cash_out' ? 'Cash Out' : tx.type || '—'}
                      color={TX_COLOR[tx.type] || 'gray'}
                    />
                  </Td>
                  <Td className="text-text-primary font-medium">{tx.customerName || tx.customer || '—'}</Td>
                  <Td className="font-mono text-xs">{tx.customerPhone || '—'}</Td>
                  <Td className={clsx('font-bold', tx.type === 'cash_in' ? 'text-green' : 'text-amber')}>
                    {tx.type === 'cash_in' ? '+' : '-'}{fmtMoney(tx.amount || 0)}
                  </Td>
                  <Td>
                    <Badge
                      label={tx.status || 'completed'}
                      color={STATUS_COLOR[tx.status] || 'green'}
                    />
                  </Td>
                  <Td className="text-text-muted text-xs max-w-[160px] truncate">
                    {tx.note || '—'}
                  </Td>
                  <Td className="text-text-muted text-xs whitespace-nowrap">{safeFmt(tx.createdAt || tx.date)}</Td>
                </tr>
              ))}
            </Table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="secondary"
            size="sm"
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </Button>
          <span className="text-sm text-text-muted">
            Page {page} of {pagination.pages}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= pagination.pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

// ── EOD Report Tab ────────────────────────────────────────────────────────────
function EODReportTab() {
  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [date, setDate]     = useState(todayStr);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const res = await agentAPI.getEODReport(date);
      setReport(res.report || null);
    } catch (err) {
      toast.error(err?.message || 'Failed to load EOD report');
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const downloadCSV = () => {
    if (!report) return;
    const rows = [
      ['DWI Fintech — Agent EOD Report'],
      ['Date', report.date || date],
      [],
      ['Metric', 'Value'],
      ['Total Cash In', report.cashIn || 0],
      ['Total Cash Out', report.cashOut || 0],
      ['Net Float Change', report.netFloat || 0],
      ['Transaction Count', report.txCount || 0],
      ['Commissions Earned', report.commissions || 0],
    ];
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `agent-eod-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV downloaded');
  };

  const netFloat = report?.netFloat ?? (report ? (report.cashIn || 0) - (report.cashOut || 0) : 0);

  return (
    <div className="flex flex-col gap-6 max-w-2xl mx-auto">
      {/* Date Picker */}
      <Card className="flex flex-col sm:flex-row sm:items-end gap-4">
        <Input
          label="Report Date"
          type="date"
          value={date}
          max={todayStr}
          onChange={(e) => setDate(e.target.value)}
          className="flex-1"
        />
        <Button variant="primary" size="md" onClick={fetchReport} loading={loading}>
          Load Report
        </Button>
      </Card>

      {/* Report Card */}
      {loading ? (
        <div className="flex justify-center py-16">
          <Spinner size="xl" />
        </div>
      ) : report ? (
        <>
          <Card accent="#06C8E8">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <p className="text-[10px] font-bold text-text-muted uppercase tracking-widest mb-1">
                  End of Day Report
                </p>
                <p className="text-text-primary font-bold text-lg">
                  {report.date
                    ? format(parseISO(report.date), 'EEEE, dd MMMM yyyy')
                    : format(new Date(date), 'EEEE, dd MMMM yyyy')}
                </p>
              </div>
              <Button variant="secondary" size="sm" onClick={downloadCSV}>
                <Download className="w-4 h-4" />
                CSV
              </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Cash In */}
              <div className="rounded-xl bg-bg-panel border border-green/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownCircle className="w-5 h-5 text-green" />
                  <p className="text-xs font-bold text-text-muted uppercase tracking-wider">Total Cash In</p>
                </div>
                <p className="text-2xl font-black text-green">{fmtMoney(report.cashIn || 0)}</p>
              </div>

              {/* Cash Out */}
              <div className="rounded-xl bg-bg-panel border border-amber/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpCircle className="w-5 h-5 text-amber" />
                  <p className="text-xs font-bold text-text-muted uppercase tracking-wider">Total Cash Out</p>
                </div>
                <p className="text-2xl font-black text-amber">{fmtMoney(report.cashOut || 0)}</p>
              </div>

              {/* Net Float */}
              <div className={clsx(
                'rounded-xl bg-bg-panel border p-4',
                netFloat >= 0 ? 'border-cyan/20' : 'border-red/20'
              )}>
                <div className="flex items-center gap-2 mb-2">
                  {netFloat >= 0
                    ? <TrendingUp className="w-5 h-5 text-cyan" />
                    : <TrendingDown className="w-5 h-5 text-red" />
                  }
                  <p className="text-xs font-bold text-text-muted uppercase tracking-wider">Net Float Change</p>
                </div>
                <p className={clsx('text-2xl font-black', netFloat >= 0 ? 'text-cyan' : 'text-red')}>
                  {netFloat >= 0 ? '+' : ''}{fmtMoney(netFloat)}
                </p>
              </div>

              {/* Commissions */}
              <div className="rounded-xl bg-bg-panel border border-purple/20 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Award className="w-5 h-5 text-purple" />
                  <p className="text-xs font-bold text-text-muted uppercase tracking-wider">Commissions</p>
                </div>
                <p className="text-2xl font-black text-purple">{fmtMoney(report.commissions || 0)}</p>
              </div>
            </div>

            {/* Tx Count */}
            <div className="mt-4 rounded-xl bg-bg-panel border border-border p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-text-muted" />
                <p className="text-text-secondary text-sm">Total Transactions</p>
              </div>
              <p className="text-text-primary font-black text-xl">{report.txCount ?? 0}</p>
            </div>

            {/* Float utilisation bar */}
            {typeof report.cashIn === 'number' && typeof report.cashOut === 'number' && (report.cashIn + report.cashOut) > 0 && (
              <div className="mt-4">
                <ProgressBar
                  pct={Math.round((report.cashIn / (report.cashIn + report.cashOut)) * 100)}
                  color="#10B981"
                  label="Cash In share of volume"
                  value={`${Math.round((report.cashIn / (report.cashIn + report.cashOut)) * 100)}%`}
                />
              </div>
            )}
          </Card>
        </>
      ) : (
        <Card>
          <Empty
            icon={<FileText className="w-10 h-10 text-text-muted" />}
            title="No report available"
            description={`No data found for ${format(new Date(date), 'dd MMM yyyy')}. Try a different date.`}
          />
        </Card>
      )}
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Agent() {
  const [tab, setTab]                 = useState('console');
  const [profile, setProfile]         = useState(null);
  const [floatData, setFloatData]     = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const fetchProfile = useCallback(async () => {
    setProfileLoading(true);
    try {
      const [profRes, floatRes] = await Promise.allSettled([
        agentAPI.getProfile(),
        agentAPI.getFloat(),
      ]);
      if (profRes.status === 'fulfilled')  setProfile(profRes.value);
      if (floatRes.status === 'fulfilled') setFloatData(floatRes.value);
      if (profRes.status === 'rejected')   toast.error('Failed to load agent profile');
    } finally {
      setProfileLoading(false);
    }
  }, []);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  return (
    <div className="min-h-screen bg-bg p-4 sm:p-6 lg:p-8">
      <div className="max-w-6xl mx-auto flex flex-col gap-6">

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <div className="flex-1">
            <h1 className="text-text-primary font-black text-2xl sm:text-3xl tracking-tight">
              Agent Portal
            </h1>
            <p className="text-text-muted text-sm mt-1">
              Cash operations, customer management and end-of-day reporting
            </p>
          </div>
          {profileLoading && <Spinner size="md" />}
        </div>

        {/* Tab Bar */}
        <div className="flex gap-2 flex-wrap">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={clsx(
                'px-4 py-2 rounded-xl text-sm font-bold transition-all duration-150 border',
                tab === t.id
                  ? 'bg-cyan/20 border-cyan text-cyan shadow-glow-cyan'
                  : 'border-border text-text-muted hover:border-border-hi hover:text-text-primary bg-bg-surface'
              )}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        {tab === 'console' && (
          <ConsoleTab
            profile={profile}
            floatData={floatData}
            profileLoading={profileLoading}
            onRefresh={fetchProfile}
          />
        )}
        {tab === 'customers' && <CustomerSearchTab />}
        {tab === 'transactions' && <TransactionsTab />}
        {tab === 'eod' && <EODReportTab />}
      </div>
    </div>
  );
}
